import { ReactElement, useEffect, useRef } from 'react';

import { useFeatureFlagEnabled, useFeatureFlagPayload } from 'posthog-js/react';
import { useLocation } from 'react-router';

import 'react-hubspot-tracking-code-hook';
import { HUBSPOT_TRACKING_ID } from '@/constants/env';
import { hubspotInit } from '@/features/Tracking/utils/hubspotInit';
import useUserDetails from '@/hooks/useUserDetails';

const HUBSPOT_PAGEVIEW_EVENTS = 'gxgo-hubspot-pageview-events-enabled';

export function HubspotTrackingLister({ children }: { readonly children: ReactElement }) {
  const initialized = useRef(false);
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);
  const isPageViewEventsEnabled = useFeatureFlagEnabled(HUBSPOT_PAGEVIEW_EVENTS);
  const pageViewEventsFilters: any = useFeatureFlagPayload(HUBSPOT_PAGEVIEW_EVENTS);

  const { userEmail } = useUserDetails();
  const sendPageView = (page: string) => {
    // Check if page title is 'GX Go' - if so, don't send pageview event
    if (document.title === 'GX Go') {
      return;
    }

    if (pageViewEventsFilters) {
      const filterRegex = new RegExp(pageViewEventsFilters.allowed_path_patterns);
      if (filterRegex.test(page)) {
        const _hsq = (window._hsq = window._hsq || []);
        _hsq.push(['setPath', page]);
        _hsq.push(['trackPageView']);
      }
    }
  };

  useEffect(() => {
    // Check if isPageViewEventsEnabled, otherwise do not initialize
    if (!isPageViewEventsEnabled) {
      return;
    }

    if (HUBSPOT_TRACKING_ID) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.id = 'hs-script-loader';
      script.async = true;
      script.defer = true;
      script.src = `//js.hs-scripts.com/${HUBSPOT_TRACKING_ID}.js`;
      document.body.appendChild(script);

      if (userEmail) {
        hubspotInit(window.location.pathname, userEmail);
        initialized.current = true;
      }
    }
  }, [userEmail, isPageViewEventsEnabled]);

  // Track page views when location changes
  useEffect(() => {
    if (!initialized.current) {
      return;
    }

    // Check if this is a new path (equivalent of PUSH)
    if (location.pathname !== prevPathRef.current) {
      sendPageView(location.pathname);
      prevPathRef.current = location.pathname;
    }
  }, [location, pageViewEventsFilters]);

  return children;
}
